
import Vue from 'vue';
import Component from 'vue-class-component';
import FileUploader from '@/components/FileUploader.vue';
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import {settings} from "@/main.ts"
import {
    ApiException,
    ApprovalStatus,
    ContactInfoViewModel,
    factory,
    Gender,
    IUpdateContactInfoRequest,
    PersonalInfoActions,
    PersonalInfoClient,
    PersonalInfoModel,
    PersonalInfoOptions,
    ResultError,
    SeafarerMaritimeInfoModel,
    UpdateContactInfoRequest,
    UpdatePersonalInfoRequest,
    UpdateSeafarerPersonalCodeModel,
    UpdateSeafarerUsernameModel
} from "@/api-client";
import {Prop} from "vue-property-decorator";
import Files from "@/components/Files.vue";
import PersonMainInfo from "@/features/personProfile/views/PersonMainInfo.vue";
import PersonFiles from "@/features/personProfile/views/PersonFiles.vue";
import {BModal} from "bootstrap-vue";
import ContactInfo from "@/features/personProfile/views/ContactInfo.vue";
import SeafarerMaritimeInfo from "@/features/personProfile/views/SeafarerMaritimeInfo.vue";
import toasts from "@/components/Toasts";
import Loader from "@/features/shared/components/Loader";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import _ from "underscore";
import ConfirmModal from "@/features/shared/components/ConfirmModal.vue";
import DetailsLayout from "@/features/personProfile/components/DetailsLayout.vue";
import userInfo from '@/features/shared/components/UserInfo';
import PopoverInfo from '@/components/PopoverInfo.vue';
import {NavigationGuardNext} from "vue-router/types/router";

interface Props {
        preLoadedModel: PersonalInfoModel;
        preLoadedContactInfoModel: ContactInfoViewModel;
        options: PersonalInfoOptions;
        preloadedAvailableActions: PersonalInfoActions;
        seafarerId: string;
    }

    @Component({
        components: {
            SectionPageHeading,
            HeadingGroup,
            FileUploader,
            Files,
            PersonMainInfo,
            PersonFiles,
            ContactInfo,
            SeafarerMaritimeInfo,
            LoadingSpinner,
            ConfirmModal,
            DetailsLayout,
            PopoverInfo
        },
        beforeRouteEnter(to, from, next: NavigationGuardNext<PersonalInfo>) {
            const props = to.meta!.props as Props;
            props.seafarerId = to.params.seafarerId;
            Promise.all([
                new PersonalInfoClient().options(to.params.seafarerId),
                new PersonalInfoClient().get(to.params.seafarerId),
                new PersonalInfoClient().ignoreNotFound().getContactInfo(to.params.seafarerId)
                    .catch((error: ApiException) => {
                        if (error.status == 404){
                            const model = factory.create(ContactInfoViewModel);
                        model.email = "";
                        model.phoneNumber = "";
                        return model;
                    }
                        throw error;
                    }),
                new PersonalInfoClient().availableActions(to.params.seafarerId)
            ]).then(values => {
                props.options = values[0];
                props.preLoadedModel = values[1];
                props.preLoadedContactInfoModel = values[2];
                props.preloadedAvailableActions = values[3];
                next();
            }).catch(e => next(e))
        }
    })
    export default class PersonalInfo extends Vue implements Props {
        @Prop({required: true})
        preLoadedModel!: PersonalInfoModel;
        @Prop({required: true})
        preLoadedContactInfoModel!: ContactInfoViewModel;
        @Prop({required: true})
        options!: PersonalInfoOptions;
        @Prop({required: true})
        preloadedAvailableActions!: PersonalInfoActions;
        @Prop({required: true})
        seafarerId!: string;
        @Prop({default: true})
        addContainerCss!: boolean;

        applicationValidationWarnings: string[] = [];
        model: PersonalInfoModel = this.preLoadedModel;
        updateModel = this.initializeUpdateModel(this.preLoadedModel);
        contactInfoModel: ContactInfoViewModel = this.preLoadedContactInfoModel;
        maritimeInfoModel: SeafarerMaritimeInfoModel | null = null;
        approvalStatuses = ApprovalStatus;
        validationErrors: ResultError = factory.create(ResultError);
        personalCodeModelValidationErrors: ResultError = factory.create(ResultError);
        usernameModelValidationErrors: ResultError = factory.create(ResultError);
        updateLoader = new Loader();
        isInfoLocked = this.preloadedAvailableActions.canEditAndApprove || this.model.approval === ApprovalStatus.Approved;
        personalCode = this.model.personalCode || "";
        username = this.model.userName;
        termsAndConditionsUrl = settings.termsAndConditionsUrl;
        availableActions: PersonalInfoActions = this.preloadedAvailableActions;
        fileLoader = new Loader();

        $refs!:{
            editModal: ConfirmModal;
            personalCodeModal: BModal;
            usernameModal: BModal;
        }

        async created(){
          if(this.$route.query.return)
          {
            this.onEditInfoClicked();
          }
          if(this.availableActions.canViewMaritimeInfo){
            this.maritimeInfoModel = await new PersonalInfoClient()
                .ignoreNotFound()
                .getMaritimeInfo(this.seafarerId)
                .catch((error: ApiException) => {
                    if (error.status == 404)
                        return factory.create(SeafarerMaritimeInfoModel);
                    throw error;
                });
          }
        }

        get isCurrentUserThePerson() : boolean {
          return this.seafarerId === userInfo.seafarerId;
        }

        async getDownloadUrl(fileId: string): Promise<string> {
            return new Promise(() => fileId);
        }

        async savePersonalInfo() {
            this.validationErrors = factory.create(ResultError);
            await new PersonalInfoClient().setLoader(this.updateLoader).update(this.updateModel, this.seafarerId)
                .then(async (result) => {
                    if(result.warnings && result.warnings.messages){
                        Object.values(result.warnings.messages).forEach((element:any) => {
                            this.applicationValidationWarnings = this.applicationValidationWarnings.concat(element);
                        });
                    }
                    this.$emit("personal-info-updated");
                    this.model = await new PersonalInfoClient().get(this.seafarerId);
                    toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_PersonalInfoSaved);
                    this.isInfoLocked = this.model.approval === ApprovalStatus.Approved;
                    await this.updateAvailableActions();

                    if(this.$route.query.return){
                        this.$router.push(`/${this.$route.query.return.toString()}`)
                    }
                }).catch(error => this.setValidationErrors(error, this.$ls.PersonProfile_PersonalInfo_Toast_Failure_PersonalInfoNotSaved));
        }

        async saveContactInfo() {
            const requestModel : IUpdateContactInfoRequest = {
              phoneNumber: this.contactInfoModel.phoneNumber,
              email: this.contactInfoModel.email,
              address: this.contactInfoModel.address
            }
            await new PersonalInfoClient().updateContactInfo(new UpdateContactInfoRequest(requestModel), this.seafarerId)
                .then(async () => {
                    this.validationErrors.clear();
                    this.contactInfoModel = await new PersonalInfoClient().getContactInfo(this.seafarerId);
                    toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_ContactDetailsSaved);
                    await this.updateAvailableActions();
                })
                .catch(error => this.setValidationErrors(error, this.$ls.PersonProfile_PersonalInfo_Toast_Failure_ContactDetailsNotSaved));
        }

        async saveMaritimeInfo() {
            this.validationErrors = factory.create(ResultError);
            if(this.maritimeInfoModel){
                await new PersonalInfoClient().updateMaritimeInfo(this.maritimeInfoModel, this.seafarerId)
                    .then(() => toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_InternalInfoSaved))
                    .catch(error => this.setValidationErrors(error, this.$ls.PersonProfile_PersonalInfo_Toast_Failure_InternalInfoNotSaved));
            }
        }

        async approve(){
            await new PersonalInfoClient().setLoader(this.updateLoader).approve(this.seafarerId, this.model.version)
                .then(async result => {
                    this.$emit("personal-info-updated");
                    this.model.approval = result;
                    toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_PersonalInfoUpdated);
                    await this.updateAvailableActions();
                    this.isInfoLocked = true;
                })
                .catch(error => this.setValidationErrors(error, this.$ls.PersonProfile_PersonalInfo_Toast_Failure_PersonalInfoNotApproved));
        }

        async reject(){
            await new PersonalInfoClient().setLoader(this.updateLoader).reject(this.seafarerId)
                .then(async result => {
                    this.$emit("personal-info-updated");
                    this.model.approval = result;
                    toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_PersonalInfoUpdated);
                    await this.updateAvailableActions();
                    this.isInfoLocked = true;
                })
                .catch(error => this.setValidationErrors(error, this.$ls.PersonProfile_PersonalInfo_Toast_Failure_PersonalInfoNotRejected));
        }

        async updateFromRR(){
            this.isInfoLocked = true;
            await new PersonalInfoClient().setLoader(this.updateLoader).updateFromRR(this.seafarerId)
                .then(success => {
                    this.$emit("personal-info-updated");
                    toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_PersonalInfoUpdated);
                    if(success.isProfileImageProvided){
                        toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_PictureUpdated);
                    }else{
                        toasts.warning(this.$ls.PersonProfile_PersonalInfo_Toast_Failure_PictureNotUpdated);
                    }
                    if(success.isSignatureImageProvided){
                        toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_SignatureImageUpdated);
                    }else{
                        toasts.warning(this.$ls.PersonProfile_PersonalInfo_Toast_Failure_SignatureImageNotUpdated);
                    }
                }).catch(error => {
                    const resultError = error as ResultError;
                    _.forEach(resultError.messagesFlattened, m => {
                        toasts.failure(m);
                    });
                });
            this.model = await new PersonalInfoClient().get(this.seafarerId);
            this.updateModel = this.initializeUpdateModel(this.model);
        }

        openPersonalCodeModal(){
            this.personalCodeModelValidationErrors = factory.create(ResultError);
            this.personalCode = this.model.personalCode || "";
            this.$refs.personalCodeModal.show();
        }

        openUsernameModal() {
            this.usernameModelValidationErrors = factory.create(ResultError);
            this.username = this.model.userName;
            this.$refs.usernameModal.show();
        }

        async updatePersonalCode() {
            if (this.validateGender() === false) {
                toasts.warning(this.$ls.Seafarer_Validation_PersonalCodeGenderDoesNotMatchProfileGender);
            }
            this.personalCodeModelValidationErrors = factory.create(ResultError);
            const model = new UpdateSeafarerPersonalCodeModel();
            model.personalCode = this.personalCode;
            await new PersonalInfoClient().updatePersonalCode(model, this.seafarerId)
                .then(success => {
                    this.$emit("personal-info-updated");
                    this.$refs.personalCodeModal.hide();
                    this.model.personalCode = success.personalCode;
                    toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_PersonalCodeUpdated);
                }).catch(failure => this.setValidationErrors(failure, this.$ls.Global_Toast_FormSaveFailure, "personalCodeModelValidationErrors"));
        }

        validateGender() {
            switch (parseInt(this.personalCode[0]) % 2) {
                case 0:
                    return this.model.gender == Gender.Female;
                case 1:
                    return this.model.gender == Gender.Male;
            }
        }

        async updateUsername() {
            this.usernameModelValidationErrors = factory.create(ResultError);
            const model = new UpdateSeafarerUsernameModel();
            model.username = this.username;
            await new PersonalInfoClient().updateUsername(model, this.seafarerId)
                .then(success => {
                    this.$emit("personal-info-updated");
                    this.$refs.usernameModal.hide();
                    this.model.userName = success.username;
                    toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_UsernameUpdated);
                }).catch(failure => this.setValidationErrors(failure, this.$ls.Global_Toast_FormSaveFailure, "usernameModelValidationErrors"));
        }

        async updateEmailNotificationPreference() {
            await new PersonalInfoClient().setLoader(this.updateLoader).updateEmailNotificationPreference(this.seafarerId, this.contactInfoModel.canReceiveEmailNotifications)
                .then(() => {
                    this.$emit("personal-info-updated");
                    toasts.success(this.$ls.PersonProfile_PersonalInfo_Toast_Success_EmailNotificationsUpdated);
                })
                .catch(error => this.setValidationErrors(error, this.$ls.PersonProfile_PersonalInfo_Toast_Failure_EmailNotificationsNotUpdated));
        }

        onEditInfoClicked(){
            if(this.availableActions.canEditAndApprove){
                this.isInfoLocked = false;
            }else{
                this.applicationValidationWarnings = [];
                this.$refs.editModal.show();
            }
        }

        initializeUpdateModel(model: PersonalInfoModel){
            const updateModel = factory.create(UpdatePersonalInfoRequest);
            updateModel.firstName = model.firstName;
            updateModel.lastName = model.lastName;
            updateModel.birthdate = model.birthdate;
            updateModel.birthPlace = model.birthPlace;
            updateModel.gender = model.gender;
            updateModel.nationality = model.nationality;
            updateModel.withoutNationality = !model.nationality;
            updateModel.version = model.version;
            // updateModel.applicationId = this.$route.que
            return updateModel;
        }

        async updateAvailableActions() {
            this.availableActions = await new PersonalInfoClient().availableActions(this.seafarerId);
        }
    }
